// components
import { Button, IconButton, Menu, Tooltip } from '@mui/material';
import type { DateView } from '@mui/x-date-pickers-pro';
import { DateCalendar, DateRangeCalendar } from '@mui/x-date-pickers-pro';
import { WeekPickerDay } from './components/WeekPickerDay';

// hooks
import { useDispatch, useSelector } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setUserPreference } from 'redux/actionCreators';
import { useDateNavigationStore } from './DateNavigation.stores';

// styles
import {
  Container,
  DateNavigationButton,
  DateNavigationMenuContainer,
  PeriodChip,
  PeriodContainer,
  PeriodSubtitle,
  PeriodTitle,
  StyledCaretIconBox,
  TodayButtonContainer
} from './DateNavigation.styles';

//  utils
import { Icon } from '@sdb/planning-ui';
import { IconContainer } from 'components/mrp/mrp.styles';
import useSelectedDate from 'components/roster-portal/activity-planning/hooks/useSelectedDate';
import { isToday } from 'date-fns';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { capitalize } from 'lodash';
import { formatByLocale } from 'utils/date';
import { StyledBoxFlexCenterGap } from '../Box/Box.styles';
import {
  getFormatByPeriod,
  nextDate,
  previousDate
} from './DateNavigation.utils';

// types
import type { Dayjs } from 'dayjs';
import type {
  DateNavigationProps,
  Period,
  SingleOrRange
} from './DateNavigation.types';
import type { WeekPickerDayProps } from './components/WeekPickerDay.types';

dayjs.extend(isoWeek);

const EMPTY_DATE_RANGE: [Date | Dayjs | null, Date | Dayjs | null] = [
  null,
  null
];

const getIsoWeekStart = (date: Date | Dayjs) => dayjs(date).startOf('isoWeek');
const getIsoWeekNumber = (date: Date | Dayjs) => dayjs(date).isoWeek();

export default function DateNavigation({
  allowMenuOptions,
  defaultDate: _defaultDate,
  defaultDateRange,
  defaultPeriod,
  disabled,
  onDateChange,
  onPeriodChange,
  options = [],
  isToolbar = false,
  isRightPositioned = false,
  hidePeriodDescription = false,
  persistPeriodPreference = true
}: DateNavigationProps) {
  'use memo';

  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const [previouslySelectedDate, setSelectedDate] = useSelectedDate();
  const { period: sharedPeriod, setPeriod } = useDateNavigationStore(
    (state) => state
  );

  // The period lives in one store shared by every DateNavigation in the app, so
  // the instance that mounted last dictates it for all of them. When the caller
  // fixes the period (no menu to choose from) we render from the prop instead:
  // a second screen staying alive — routine while dojo swaps views — must not
  // turn this screen's year navigation into month navigation.
  const period =
    !allowMenuOptions && defaultPeriod ? defaultPeriod : sharedPeriod;
  const [dateRangeAnchor, setDateRangeAnchor] =
    useState<HTMLButtonElement | null>(null);
  const datePickerOpen = Boolean(dateRangeAnchor);

  const holidays = useSelector((state) => state.holidays);
  const defaultDateNavigationPeriod = useSelector(
    (state) => state.preferences.defaultDateNavigationPeriod
  );

  const defaultDate = _defaultDate || previouslySelectedDate;
  const isDisabled = disabled || !period;

  let showIsHoliday = false;
  let showIsToday = false;
  let dayDescription = '';

  if (period == 'day') {
    const holiday = holidays[formatByLocale(defaultDate, 'yyyy-MM-dd')];
    showIsHoliday = Boolean(holiday);
    showIsToday = isToday(defaultDate);
    dayDescription = showIsToday ? t('today') : holiday?.description || '';
  }

  const handleDateChange = (date: SingleOrRange | null) => {
    if (!Array.isArray(date) && date && period !== 'custom') {
      setSelectedDate(date as Date);
    }

    if (
      (period === 'fourWeeks' || period === 'eightWeeks') &&
      date &&
      !Array.isArray(date)
    ) {
      onDateChange?.(getIsoWeekStart(date).toDate());
    } else {
      onDateChange?.(date);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDateRangeAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setDateRangeAnchor(null);
  };

  const savePreference = (period: Period) => {
    dispatch(
      setUserPreference({
        key: 'defaultDateNavigationPeriod',
        value: period
      })
    );
  };

  const dateCalendarView = (period: Period | null): readonly DateView[] => {
    switch (period) {
      case 'year':
        return ['year'];
      case 'month':
        return ['month', 'year'];
      case 'fourWeeks':
      case 'eightWeeks':
        return ['day', 'month', 'year'];
      default:
        return ['day'];
    }
  };

  const dateForLabel =
    period === 'custom'
      ? defaultDateRange ?? undefined
      : defaultDate ?? undefined;
  const periodLabel = dateForLabel
    ? capitalize(getFormatByPeriod(dateForLabel, period))
    : '';

  const periodDescription = (showWeek = false) => {
    let startWeek: number;
    let endWeek: number;

    switch (period) {
      case 'day':
        startWeek = getIsoWeekNumber(defaultDate);
        return (
          `${capitalize(formatByLocale(defaultDate, 'EEEE'))}` +
          (showWeek ? ` (${capitalize(t('week'))} ${startWeek})` : '')
        );
      case 'week':
      case 'workweek':
        startWeek = getIsoWeekNumber(defaultDate);
        return capitalize(t(period)) + (showWeek ? ` ${startWeek}` : '');
      case 'fourWeeks':
        startWeek = getIsoWeekNumber(getIsoWeekStart(defaultDate));
        endWeek = getIsoWeekNumber(getIsoWeekStart(defaultDate).add(27, 'day'));
        return (
          capitalize(t(period)) +
          (showWeek
            ? ` (${capitalize(t('week'))} ${startWeek} - ${endWeek})`
            : '')
        );
      case 'eightWeeks':
        startWeek = getIsoWeekNumber(getIsoWeekStart(defaultDate));
        endWeek = getIsoWeekNumber(getIsoWeekStart(defaultDate).add(55, 'day'));
        return (
          capitalize(t(period)) +
          (showWeek
            ? ` (${capitalize(t('week'))} ${startWeek} - ${endWeek})`
            : '')
        );
      case 'month':
        startWeek = getIsoWeekNumber(dayjs(defaultDate).startOf('month'));
        endWeek = getIsoWeekNumber(dayjs(defaultDate).endOf('month'));
        return (
          capitalize(t(period)) +
          (showWeek
            ? ` (${capitalize(t('week'))} ${startWeek} - ${endWeek})`
            : '')
        );
      default:
        return capitalize(t(period ?? ''));
    }
  };

  // Set initial period if passed
  useEffect(() => {
    // Without menu options, or when this view keeps its period to itself, start on `defaultPeriod`
    // rather than restoring the app-wide saved preference — so another view's saved period cannot force
    // this one into it.
    if (!allowMenuOptions || !persistPeriodPreference) {
      return setPeriod(defaultPeriod);
    }

    if (defaultDateNavigationPeriod || defaultPeriod) {
      setPeriod(
        options?.includes(defaultDateNavigationPeriod)
          ? defaultDateNavigationPeriod
          : defaultPeriod
      );
    }
    // `options` is intentionally excluded from deps: it is only read to validate the saved preference,
    // not a trigger for this effect. Callers (e.g. Toolbar) pass it as an inline array literal, so
    // including it would cause the effect to fire every render and produce an infinite update loop
    // via the Zustand store → RosterPlan re-render → MUI DataGrid cascading Store.setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultDateNavigationPeriod,
    defaultPeriod,
    allowMenuOptions,
    persistPeriodPreference,
    setPeriod
  ]);

  useEffect(() => {
    if (period) onPeriodChange?.(period);
  }, [onPeriodChange, period]);

  return (
    <Container isToolbar={isToolbar} isRightPositioned={isRightPositioned}>
      {period === 'day' && (
        <Tooltip title={t('previousWeek') || ''} arrow>
          <IconButton
            color="inherit"
            className="prev-btn"
            size="small"
            onClick={previousDate.bind(
              null,
              defaultDate,
              'week',
              handleDateChange
            )}
            disabled={isDisabled}
          >
            <Icon type="angles-left" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {period !== 'custom' && !isRightPositioned && (
        <Tooltip
          title={
            t(
              'previous' +
                (period === 'fourWeeks' || period === 'eightWeeks'
                  ? 'Week'
                  : capitalize(period?.toString()))
            ) || ''
          }
          arrow
        >
          <IconButton
            className="prev-btn"
            color="inherit"
            size="small"
            onClick={previousDate.bind(
              null,
              defaultDate,
              period as Period,
              handleDateChange
            )}
            disabled={isDisabled}
          >
            <Icon type="chevron-left" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={dayDescription} arrow>
        <DateNavigationButton
          variant="text"
          showIsToday={showIsToday}
          showIsHoliday={showIsHoliday}
          startIcon={<Icon type="calendar-week" />}
          {...(allowMenuOptions &&
            !isRightPositioned && {
              endIcon: (
                <IconContainer iconSize={isToolbar ? 'SMALL' : 'MEDIUM'}>
                  <StyledCaretIconBox datePickerOpen={datePickerOpen}>
                    <Icon
                      type={isToolbar ? 'chevron-down' : 'caret-down'}
                      fontSize="small"
                    />
                  </StyledCaretIconBox>
                </IconContainer>
              )
            })}
          disabled={isDisabled}
          onClick={handleClick}
          isToolbar={isToolbar}
          isRightPositioned={isRightPositioned}
        >
          {!isRightPositioned && (
            <PeriodContainer isToolbar={isToolbar}>
              {!hidePeriodDescription &&
                (isToolbar ? (
                  <PeriodTitle>{periodDescription(false)}</PeriodTitle>
                ) : (
                  <PeriodSubtitle>{periodDescription(true)}</PeriodSubtitle>
                ))}

              <PeriodTitle>{periodLabel}</PeriodTitle>
            </PeriodContainer>
          )}
        </DateNavigationButton>
      </Tooltip>

      <Menu
        anchorEl={dateRangeAnchor}
        open={datePickerOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {allowMenuOptions && (
          <DateNavigationMenuContainer>
            {options.map((periodOption: Period, i: number) => (
              <PeriodChip
                variant={period == periodOption ? 'filled' : 'outlined'}
                key={i}
                onClick={() => {
                  setPeriod(periodOption);
                  if (persistPeriodPreference) savePreference(periodOption);
                }}
                label={t(`timeUnits.${periodOption.toUpperCase()}`)}
              />
            ))}
          </DateNavigationMenuContainer>
        )}

        {period === 'custom' ? (
          <DateRangeCalendar
            key={period}
            calendars={2}
            value={defaultDateRange ?? EMPTY_DATE_RANGE}
            displayWeekNumber
            onChange={(date) => {
              handleDateChange(date);
            }}
          />
        ) : (
          <DateCalendar
            key={period}
            onChange={(date) => {
              handleDateChange(date);
              setDateRangeAnchor(null);
            }}
            value={defaultDate ?? null}
            disabled={isDisabled}
            displayWeekNumber
            views={dateCalendarView(period)}
            slots={{
              day:
                period === 'week' ||
                period === 'workweek' ||
                period === 'fourWeeks' ||
                period === 'eightWeeks'
                  ? WeekPickerDay
                  : undefined
            }}
            slotProps={{
              day: {
                anchor: defaultDate,
                workingWeek: period === 'workweek',
                fourWeeks: period === 'fourWeeks',
                eightWeeks: period === 'eightWeeks',
                weekStartsOn: 1 // monday
              } as WeekPickerDayProps
            }}
          />
        )}
        <StyledBoxFlexCenterGap>
          <TodayButtonContainer>
            <Button
              variant="text"
              onClick={() => {
                if (period === 'custom') {
                  handleDateChange([new Date(), null]);
                } else {
                  handleDateChange(new Date());
                  setDateRangeAnchor(null);
                }
              }}
            >
              {t('today')}
            </Button>
          </TodayButtonContainer>
        </StyledBoxFlexCenterGap>
      </Menu>

      {period !== 'custom' && !isRightPositioned && (
        <Tooltip
          title={
            t(
              'next' +
                (period === 'fourWeeks' || period === 'eightWeeks'
                  ? 'Week'
                  : capitalize(period?.toString()))
            ) || ''
          }
          arrow
          enterDelay={300}
          enterNextDelay={300}
        >
          <IconButton
            color="inherit"
            className="next-btn"
            size="small"
            onClick={nextDate.bind(
              null,
              defaultDate,
              period as Period,
              handleDateChange
            )}
            disabled={isDisabled}
          >
            <Icon type="chevron-right" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {period === 'day' && (
        <Tooltip title={t('nextWeek') || ''} arrow>
          <IconButton
            color="inherit"
            className="next-btn"
            size="small"
            onClick={nextDate.bind(null, defaultDate, 'week', handleDateChange)}
            disabled={isDisabled}
          >
            <Icon type="angles-right" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Container>
  );
}
